import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import BrandLogo from '../components/BrandLogo';
import PrimaryButton from '../components/PrimaryButton';
import TextField from '../components/TextField';
import { mapSolicitarOTPError, solicitarOTP } from '../services/authService';
import { useAuthStore } from '../store/authStore';

function validateEmail(email) {
  if (!email.trim()) {
    return 'El email es requerido';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    return 'Ingresá un email válido';
  }

  return '';
}

function LoginPage() {
  const navigate = useNavigate();
  const setLoading = useAuthStore((state) => state.setLoading);
  const setStoreError = useAuthStore((state) => state.setError);
  const isLoading = useAuthStore((state) => state.isLoading);
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [apiError, setApiError] = useState('');
  const validationError = useMemo(() => validateEmail(email), [email]);
  const visibleError = touched ? validationError || apiError : apiError;
  const isDisabled = isLoading || !email.trim() || Boolean(validationError);

  const handleChange = (event) => {
    setEmail(event.target.value);
    setTouched(true);
    setApiError('');
    setStoreError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    setApiError('');
    setStoreError(null);

    if (validationError) {
      return;
    }

    setLoading(true);

    try {
      await solicitarOTP({ email: email.trim().toLowerCase() });
      navigate('/login/verificar', {
        state: { email: email.trim().toLowerCase() },
      });
    } catch (error) {
      const message = mapSolicitarOTPError(error);
      setApiError(message);
      setStoreError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex min-h-screen items-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto grid w-full max-w-[1100px] items-center gap-10 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="hidden lg:block">
          <div className="max-w-[420px]">
            <p className="font-login text-[13px] font-bold uppercase tracking-[0.26em] text-[#8390a3]">
              Eva Ops
            </p>
            <h1 className="mt-4 font-login text-[42px] font-bold leading-[1.05] text-[#111111]">
              Acceso a la plataforma operativa.
            </h1>
            <p className="mt-5 max-w-[360px] font-login text-[16px] leading-7 text-[#5f6673]">
              Ingresá tu email corporativo y te enviaremos un código de acceso
              para continuar.
            </p>
          </div>
        </div>

        <div className="w-full rounded-[24px] border border-[#d7dce2] bg-white px-5 py-6 shadow-[0_22px_65px_rgba(15,23,42,0.12)] sm:mx-auto sm:max-w-[420px] sm:px-7 sm:py-8">
          <div className="border-b border-[#edf0f4] pb-5">
            <BrandLogo />
          </div>

          <div className="pt-10 sm:pt-12">
            <form className="space-y-3" onSubmit={handleSubmit} noValidate>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="email@empresa.com"
                value={email}
                onChange={handleChange}
                onBlur={() => setTouched(true)}
                autoComplete="email"
              />

              {visibleError ? (
                <p className="pt-1 font-login text-[13px] text-[#c43c2f]">
                  {visibleError}
                </p>
              ) : null}

              <div className="pt-5">
                <PrimaryButton type="submit" disabled={isDisabled}>
                  {isLoading ? 'Enviando código...' : 'Enviar código'}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoginPage;
